/**
 * Pure decisions for the top menu bar's dropdown panels: which sub-columns a
 * wide panel lays out into, and keyboard movement inside an open panel.
 *
 * IN `lib/` AND NOT IN THE COMPONENT, for the same reason `views.js` sits
 * here: `npm test -w web` runs node's own test runner over
 * `src/lib/*.test.js`, and node cannot parse JSX. Every DECISION the menu
 * makes has to leave the component and become a plain function, or it can
 * only ever be checked by eye.
 */

/**
 * Sub-grouping for wide dropdown panels, keyed by a `SECTIONS` heading from
 * views.js. Only `Capture` is wide enough to need columns; `Statements` and
 * `Set up` render as a single column each, so they have no entry here.
 *
 * A SIDE TABLE, DELIBERATELY NOT A CHANGE TO `SECTIONS`: `SECTIONS` is the
 * single ordered authority for which screens exist and where — six tests in
 * views.test.js read its `[heading, [[id, label], ...]]` shape directly,
 * including a `SECTIONS.find(([, items]) => items.some(([id]) => id === view))`
 * lookup that a nested shape would break. This table only says how `Capture`'s
 * existing ids are laid out sideways into columns; it names no screen
 * `SECTIONS` does not already have, and adding a screen still means adding
 * exactly one line, in one file, as it does today.
 */
export const MENU_COLUMNS = {
  Capture: [
    ['Daily',   ['journal', 'invoice', 'payment']],
    ['Bank',    ['cash-book', 'bank-reconciliation', 'slips', 'slip-review', 'allocation-rules',
                'description-rules', 'reference-prefixes']],
    ['Stock',   ['items', 'stock-on-hand', 'stock-take', 'stock-adjustments']],
    ['Records', ['documents', 'contacts']],
  ],
};

/**
 * Lay an already-permission-filtered list of `[id, label]` pairs out into the
 * columns a dropdown panel renders for one `SECTIONS` heading.
 *
 * `visible` must already have been through `canView` — this function never
 * calls it and must not, because it fails in the OPPOSITE direction to
 * `canView`. `canView` decides PERMISSION: a forgotten line there has to hide
 * a screen from someone who should not reach it, silently, because the
 * failure the app cannot afford is leaking a tool nobody granted. This
 * function decides LAYOUT: a forgotten or renamed id here must NOT hide a
 * screen the caller already decided the user may reach. Instead every id in
 * `visible` that no column names falls through into a trailing, unlabelled
 * column — still there, still clickable, just in the wrong place, which is a
 * visible UI bug someone will notice and file rather than a silent access
 * bug nobody will ever see.
 *
 * Empty columns are dropped, including the trailing one and the case where
 * `visible` itself is empty.
 */
export function menuColumns(heading, visible) {
  const groups = MENU_COLUMNS[heading];

  // No sub-grouping declared for this heading (Statements, Set up): everything
  // that survived canView renders in a single unlabelled column, unless there
  // is nothing to show at all.
  if (!groups) return visible.length ? [[null, visible]] : [];

  const placed = new Set();
  const columns = [];
  for (const [label, ids] of groups) {
    // Walk the column's OWN id order, not `visible`'s, so a column's contents
    // are stable regardless of what order the caller happened to filter in.
    const items = ids
      .map((id) => visible.find(([vid]) => vid === id))
      .filter(Boolean);
    for (const [id] of items) placed.add(id);
    if (items.length) columns.push([label, items]);
  }

  // Anything visible that no column claimed — a fabricated or newly-added id
  // this table has not caught up with yet — in the order the caller gave it.
  const leftover = visible.filter(([id]) => !placed.has(id));
  if (leftover.length) columns.push([null, leftover]);

  return columns;
}

/**
 * Pure keyboard movement inside an open panel: given the key pressed, the
 * currently focused index (`-1` for "nothing focused yet"), and how many
 * items the panel holds, returns the index to move focus to, or `null`.
 *
 * `null` FOR ANY KEY THIS FUNCTION DOES NOT RECOGNISE IS LOAD-BEARING, not a
 * default that happened to be convenient. The component only calls
 * `preventDefault()` on the key event when this returns a number. If an
 * unhandled key like Tab returned `current` instead of `null`, the handler
 * would swallow that keydown and trap focus inside the menu — Tab would
 * silently do nothing rather than moving focus out to the next control.
 */
export function nextIndex(key, current, count) {
  if (count <= 0) return null;

  switch (key) {
    case 'ArrowDown':
      // current === -1 (nothing focused) lands on 0, same as wrapping past
      // the last item — both mean "the next item after none is the first".
      return current >= count - 1 ? 0 : current + 1;
    case 'ArrowUp':
      // current === -1 wraps to the last item, same as wrapping past the
      // first — both mean "the item before none is the last".
      return current <= 0 ? count - 1 : current - 1;
    case 'Home':
      return 0;
    case 'End':
      return count - 1;
    default:
      return null;
  }
}

/** True only for the key that closes an open panel without choosing anything. */
export function isDismiss(key) {
  return key === 'Escape';
}

/**
 * True for Enter and Space — the keys that activate a focused item, and,
 * separately, the trigger that opens the panel in the first place.
 *
 * A native `<button>` already fires a `click` event for both of these keys on
 * its own, so this function is not needed to activate an item inside an
 * already-open panel — the browser does that. It exists for the TRIGGER
 * button: on `click` the component must still decide to open the dropdown and
 * move focus to its first item, which nothing does automatically, so it needs
 * an explicit check for the keys that are about to produce that click.
 */
export function isActivate(key) {
  return key === 'Enter' || key === ' ';
}
